import { ArrowDownRight, ArrowUpRight, ArrowLeftRight } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import BarFill from '@/components/charts/BarFill'
import { CHART_TEXT_XS, chartTheme, seriesColor } from '@/components/charts/chart-theme'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import { barShare, compactDigits, shownValue } from '@/features/dashboard/lib/format'
import { pageOf, usePage } from '@/features/dashboard/lib/page'
import {
  STEP_NOUN,
  SHOW_WORDS,
  coversWords,
  dateWords,
  lastValue,
  lineWay,
  showOf,
  spanWords,
  stepRows,
  tickWords,
  timeWords,
  type Way,
} from '@/features/dashboard/lib/time'
import type {
  Drawn,
  Fix,
  Series,
  TimeEvery,
  TimeShow,
  TimeVocabulary,
} from '@/features/mitra/api/dashboard'
import { toChartNumber, money } from '@/lib/money'
import { useUiStore } from '@/lib/ui-store'
import { cn } from '@/lib/utils'
import {
  CoverageMeter,
  Head,
  Meta,
  Pager,
  Picture,
  Unreadable,
  WarningChip,
} from '@/screens/dashboards/components/WidgetCards'

/**
 * A widget over time or over brokers (mitra-ai "time and brokers"): the same
 * card as every other widget -- title, the formula's picture, coverage --
 * with what it is over said once under the title (the window, how it steps,
 * the trading days it actually covers), the figures drawn as a line, bars or
 * a table, and the server's note of what they show underneath.
 *
 * EVERY FIGURE IS THE ENGINE'S. A point's height is drawing only (the
 * engine's string turned into a float for the plot); every digit printed --
 * a tooltip, a table cell, a legend's last value -- is the engine's own
 * string through `shownValue`. Green and red say which way a step went by
 * comparing two of those strings, and print no move of their own: the note
 * says by how much.
 *
 * A WIDGET THE DATA CANNOT FILL says why (`reason`) where its figures would
 * be, as a widget whose formula does not read says its error: nothing is
 * drawn empty and nothing is drawn as zero.
 */

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const WAY_TEXT: Record<Exclude<Way, null>, string> = {
  up: 'text-market-up',
  down: 'text-market-down',
  flat: 'text-muted-foreground',
}

const WAY_WORDS: Record<'up' | 'down', string> = {
  up: 'rose',
  down: 'fell',
}

/** Which way a line went, as an arrow; none where it did not move -- a flat arrow says nothing. */
function WayIcon({ way }: { way: Way }) {
  if (way === null || way === 'flat') return null
  const Icon = way === 'up' ? ArrowUpRight : ArrowDownRight
  return <Icon aria-hidden="true" className={cn('size-4 shrink-0', WAY_TEXT[way])} />
}

/** A plot coordinate from the engine's string: drawing only, never printed. */
const plotted = (value: string | null): number | null =>
  value === null ? null : toChartNumber(money(value))

/** The steps oldest first, one key per line, as the chart takes them. */
function chartRows(series: readonly Series[]) {
  const rows = stepRows(series).reverse()
  return rows.map((row) => {
    const out: Record<string, number | string | null> = { date: row.date }
    row.cells.forEach((cell, i) => {
      const y = plotted(cell.value)
      out[`s${String(i)}`] = y
      out[`v${String(i)}`] = cell.value
      // One line's bars split by sign and stacked from zero: below zero is red.
      out[`p${String(i)}`] = y !== null && y >= 0 ? y : null
      out[`n${String(i)}`] = y !== null && y < 0 ? y : null
    })
    return out
  })
}

/** The digits on an axis: the engine's unit, cut short by moving digits. */
const axisWords = (value: number): string => {
  if (!Number.isFinite(value)) return ''
  const fixed = Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(2)
  return compactDigits(fixed)
}

function TooltipBody({
  active,
  payload,
  widget,
  every,
}: {
  active?: boolean | undefined
  payload?: readonly { payload?: unknown }[] | undefined
  widget: Drawn
  every: TimeEvery | null
}) {
  const raw = payload?.[0]?.payload
  if (!active || typeof raw !== 'object' || raw === null) return null
  const row = raw as Record<string, unknown>
  const series = widget.series ?? []
  return (
    <div className="rounded-md border border-border-subtle bg-card px-3 py-2 text-label-s shadow-sm">
      <p className="text-muted-foreground">{tickWords(String(row.date), every)}</p>
      <ul className="mt-1 flex flex-col gap-1">
        {series.map((line, i) => (
          <li key={line.label} className="flex justify-between gap-3 tabular-nums">
            {series.length > 1 ? <span>{line.label}</span> : null}
            <span className="text-foreground">
              {shownValue((row[`v${String(i)}`] as string | null | undefined) ?? null, widget.unit)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A series as a line or as bars, one colour per line; a gap stays a gap. */
function SeriesChart({ widget, show }: { widget: Drawn; show: 'line' | 'bars' }) {
  // Subscribing re-reads the tokens after a theme change.
  useUiStore((s) => s.theme)
  useUiStore((s) => s.colorblind)
  const t = chartTheme()
  const series = useMemo(() => widget.series ?? [], [widget.series])
  const data = useMemo(() => chartRows(series), [series])
  const every = widget.time?.every ?? null
  const one = series.length === 1
  const axis = {
    stroke: t.axis,
    tick: { fill: t.axis, fontSize: CHART_TEXT_XS },
    tickLine: false,
    axisLine: false,
  }
  const x = (
    <XAxis
      dataKey="date"
      {...axis}
      minTickGap={24}
      tickFormatter={(date: string) => tickWords(date, every)}
    />
  )
  const y = <YAxis {...axis} width={56} tickFormatter={axisWords} />
  const tip = (
    <Tooltip
      cursor={{ stroke: t.grid, fill: t.grid }}
      content={(props) => (
        <TooltipBody active={props.active} payload={props.payload} widget={widget} every={every} />
      )}
    />
  )
  return (
    <ResponsiveContainer width="100%" height={200}>
      {show === 'line' ? (
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={t.grid} vertical={false} />
          {x}
          {y}
          {tip}
          {series.map((line, i) => (
            <Line
              key={line.label}
              dataKey={`s${String(i)}`}
              name={line.label}
              type="monotone"
              stroke={seriesColor(t, i)}
              strokeWidth={2}
              dot={false}
              // A missing step is a gap in the line, never a fall to zero.
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      ) : (
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={t.grid} vertical={false} />
          {x}
          {y}
          {tip}
          {/* One line's bars below zero are red, so a net or a loss reads at a glance. */}
          {one
            ? [
                <Bar
                  key="p"
                  dataKey="p0"
                  stackId="sign"
                  fill={t.accent}
                  isAnimationActive={false}
                />,
                <Bar key="n" dataKey="n0" stackId="sign" fill={t.down} isAnimationActive={false} />,
              ]
            : series.map((line, i) => (
                <Bar
                  key={line.label}
                  dataKey={`s${String(i)}`}
                  name={line.label}
                  fill={seriesColor(t, i)}
                  isAnimationActive={false}
                />
              ))}
        </BarChart>
      )}
    </ResponsiveContainer>
  )
}

/** Each line's name and its last figure, with which way it went over the window. */
function SeriesLegend({ widget }: { widget: Drawn }) {
  useUiStore((s) => s.theme)
  const series = widget.series ?? []
  const t = series.length > 1 ? chartTheme() : null
  return (
    <ul data-role="series-legend" className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-label-s">
      {series.map((line, i) => {
        const way = lineWay(line)
        return (
          <li
            key={line.label}
            data-role="series-last"
            data-way={way ?? undefined}
            className="inline-flex items-center gap-1"
          >
            {t ? (
              <span
                aria-hidden="true"
                className="size-2 rounded-xs"
                style={{ background: seriesColor(t, i) }}
              />
            ) : null}
            {widget.kind === 'number' && series.length === 1 ? null : (
              <span className="text-foreground/80">{line.label}</span>
            )}
            <span className="text-foreground tabular-nums">
              {shownValue(lastValue(line), widget.unit)}
            </span>
            <WayIcon way={way} />
            {way && way !== 'flat' ? (
              <span className="sr-only">{WAY_WORDS[way]} over the window</span>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

/** A series as a table: a row per step, newest first, a column per line. */
function SeriesTable({ widget }: { widget: Drawn }) {
  const series = widget.series ?? []
  const rows = useMemo(() => stepRows(widget.series ?? []), [widget.series])
  const [page, setPage] = usePage([widget.series])
  const every = widget.time?.every ?? null
  return (
    <>
      <div className="mt-3 overflow-x-auto">
        <table data-role="series-table" className="w-full border-collapse text-body-s">
          <thead>
            <tr className="border-b border-border-subtle text-left text-muted-foreground">
              <th scope="col" className="py-2 pr-3 font-medium">
                {every === 'month' ? 'Month' : every === 'week' ? 'Week to' : 'Date'}
              </th>
              {series.map((line) => (
                <th key={line.label} scope="col" className="px-3 py-2 text-right font-medium">
                  {series.length === 1 && widget.kind === 'number' ? widget.title : line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageOf(rows, page).map((row) => (
              <tr key={row.date} className="border-b border-border-subtle last:border-0">
                <th
                  scope="row"
                  title={row.from !== row.date ? spanWords(row.from, row.date) : undefined}
                  className="py-2 pr-3 text-left font-medium whitespace-nowrap text-foreground"
                >
                  {every === 'month' ? tickWords(row.date, every) : dateWords(row.date)}
                </th>
                {row.cells.map((cell, i) => (
                  <td
                    key={series[i]?.label ?? i}
                    data-way={cell.way ?? undefined}
                    title={
                      cell.value === null && cell.missing.length
                        ? `No ${cell.missing.join(', ')} on record`
                        : undefined
                    }
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      cell.value === null
                        ? 'text-muted-foreground'
                        : cell.way
                          ? WAY_TEXT[cell.way]
                          : 'text-foreground',
                    )}
                  >
                    {shownValue(cell.value, widget.unit)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={rows.length} onPage={setPage} />
    </>
  )
}

/** Line, bars or table: the engine's hint first, then the reader's choice. */
function ShowPicker({
  title,
  value,
  onChange,
}: {
  title: string
  value: TimeShow
  onChange: (show: TimeShow) => void
}) {
  return (
    <div role="radiogroup" aria-label={`Show ${title} as`} className="flex gap-1">
      {(['line', 'bars', 'table'] as const).map((show) => (
        <button
          key={show}
          type="button"
          role="radio"
          aria-checked={show === value}
          data-show={show}
          onClick={() => {
            onChange(show)
          }}
          className={cn(
            'min-h-8 rounded-full px-3 text-label-s',
            show === value
              ? 'bg-foreground text-background'
              : 'bg-muted text-foreground hover:bg-muted/70',
            FOCUS,
          )}
        >
          {SHOW_WORDS[show]}
        </button>
      ))}
    </div>
  )
}

/**
 * Brokers (or any rows over a window) ranked as the engine ranked them: a
 * rank, the name, a bar against the largest either way, the engine's digits.
 * Bought more than sold reads green, sold more red, where the figure can be
 * either; a figure that is never below zero (bought, a share) is one colour.
 */
function RankedRows({ widget }: { widget: Drawn }) {
  const points = widget.points ?? []
  const values = points.map((p) => p.value)
  const signed = values.some((v) => v?.startsWith('-') ?? false)
  const [page, setPage] = usePage([widget.points])
  return (
    <>
      <ol aria-label={widget.title} data-role="ranked-rows" className="mt-3 flex flex-col gap-2">
        {pageOf(points, page).map((point, index) => {
          const share = barShare(point.value, values)
          const negative = point.value?.startsWith('-') ?? false
          return (
            <li
              key={point.label}
              data-role="ranked-row"
              data-way={signed && point.value !== null ? (negative ? 'down' : 'up') : undefined}
              className="grid grid-cols-[--spacing(6)_minmax(0,--spacing(36))_minmax(0,1fr)_auto] items-center gap-2 text-body-s"
            >
              <span className="text-right text-label-s text-muted-foreground tabular-nums">
                {String(page * 10 + index + 1)}
              </span>
              <span className="truncate text-foreground" title={point.label}>
                {point.label}
              </span>
              <span aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-track">
                {point.value === null ? null : (
                  <BarFill
                    ratio={share}
                    index={index}
                    className={
                      negative
                        ? 'bg-market-down-fill'
                        : signed
                          ? 'bg-market-up-fill'
                          : 'bg-tone-green'
                    }
                  />
                )}
              </span>
              <span
                className={cn(
                  'text-right tabular-nums',
                  point.value === null
                    ? 'text-muted-foreground'
                    : signed
                      ? negative
                        ? 'text-market-down'
                        : 'text-market-up'
                      : 'text-foreground',
                  share === 1 && 'font-semibold',
                )}
              >
                {shownValue(point.value, widget.unit)}
              </span>
            </li>
          )
        })}
      </ol>
      <Pager page={page} total={points.length} onPage={setPage} />
    </>
  )
}

/** What the rows are: brokers in these companies, or over the whole market. */
function brokersWords(widget: Drawn): string | null {
  if (widget.scope.kind !== 'brokers') return null
  return widget.scope.symbols.length
    ? `Brokers in ${widget.scope.symbols.join(', ')}`
    : 'Brokers, whole market'
}

/** The window, the subject and the days covered, as one quiet line under the title. */
export function WindowCaption({
  widget,
  vocabulary,
}: {
  widget: Drawn
  vocabulary?: TimeVocabulary | null | undefined
}) {
  const parts = [
    brokersWords(widget),
    widget.time ? timeWords(widget.time, vocabulary) : null,
    coversWords(widget),
  ].filter((p): p is string => p !== null)
  if (parts.length === 0) return null
  return (
    <p
      data-role="window-caption"
      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-label-s text-muted-foreground"
    >
      {widget.scope.kind === 'brokers' ? (
        <ArrowLeftRight aria-hidden="true" className="size-4 shrink-0" />
      ) : null}
      {parts.join(' · ')}
    </p>
  )
}

/** The server's sentence of what the figures show, under them. */
export function WidgetNote({ widget }: { widget: Drawn }) {
  if (!widget.note) return null
  return (
    <p data-role="widget-note" className="mt-3 text-body-s text-foreground/80">
      {widget.note}
    </p>
  )
}

/** Partial coverage in words: never an error, always said. */
function Warnings({ widget }: { widget: Drawn }) {
  if (!widget.warnings?.length) return null
  return (
    <ul data-role="window-warnings" className="mt-2 flex flex-col gap-1 text-label-s text-warning">
      {widget.warnings.map((w) => (
        <li key={w}>{w}</li>
      ))}
    </ul>
  )
}

export function WindowCard({
  widget,
  actions = null,
  onApplyFix,
  vocabulary,
}: {
  widget: Drawn
  actions?: ReactNode
  onApplyFix?: ((fix: Fix) => void) | undefined
  vocabulary?: TimeVocabulary | null | undefined
}) {
  const [show, setShow] = useState<TimeShow>(showOf(widget))
  const series = widget.series
  const every = widget.time?.every ?? null
  const noun = series
    ? every
      ? STEP_NOUN[every]
      : 'steps'
    : widget.scope.kind === 'brokers'
      ? 'brokers'
      : 'companies'
  let body: ReactNode
  if (widget.error) {
    body = <Unreadable widget={widget} />
  } else if (widget.reason) {
    body = (
      <p role="status" data-role="window-reason" className="mt-3 text-body-s text-muted-foreground">
        {widget.reason}
      </p>
    )
  } else if (series) {
    body = (
      <>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {widget.coverage ? <CoverageMeter coverage={widget.coverage} noun={noun} /> : null}
          <WarningChip check={widget.check} onApply={onApplyFix} />
          <span className="flex-1" />
          <ShowPicker title={widget.title} value={show} onChange={setShow} />
        </div>
        {show === 'table' ? (
          <SeriesTable widget={widget} />
        ) : (
          <figure
            data-role="series-chart"
            data-show={show}
            role="img"
            aria-label={widget.note ?? widget.title}
            className="mt-3 min-w-0"
          >
            <SeriesChart widget={widget} show={show} />
          </figure>
        )}
        <SeriesLegend widget={widget} />
      </>
    )
  } else if (widget.kind === 'number') {
    body = (
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <p
          data-role="tile-value"
          className="min-w-fit flex-1 text-h-l whitespace-nowrap text-foreground tabular-nums"
        >
          {shownValue(widget.value ?? null, widget.unit)}
        </p>
        {widget.coverage ? <CoverageMeter coverage={widget.coverage} noun={noun} /> : null}
      </div>
    )
  } else {
    body = (
      <>
        <Meta widget={widget} noun={noun} onApplyFix={onApplyFix} />
        <RankedRows widget={widget} />
      </>
    )
  }
  return (
    <article
      id={widget.id ? `measure-${widget.id}` : undefined}
      data-role="dash-window"
      data-scope={widget.scope.kind}
      className={cn(SECTION_CARD, 'min-w-0', series && 'col-span-full')}
    >
      <Head widget={widget} actions={actions} />
      <WindowCaption widget={widget} vocabulary={vocabulary} />
      {widget.error ? null : <Picture widget={widget} />}
      {body}
      {widget.error || widget.reason ? null : (
        <>
          <Warnings widget={widget} />
          <WidgetNote widget={widget} />
        </>
      )}
    </article>
  )
}
